import { forwardRef, useDeferredValue } from "react";
import { useEffect } from "react";
import { useImperativeHandle } from "react";
import { useRef } from "react";
import { useState } from "react";
import { Cell } from "../Cell";
import classes from "./WordControl.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { goToQuestion } from "@educaplay/store/slices/gameSlice";
import { findNextFocusIndex, findPreviousFocusIndex } from "../../../utils/focus";
import { SCREENS } from "@educaplay/store/constants";
import { getLastCharacter, NAME_SOUND_ACTIVE, useGameSounds } from "@educaplay/core";
import { CRUCIGRAMA_SOUNDS } from "../../../utils/constants";
import { normaliceWord } from "../../../utils/correction";

export const WordControl = forwardRef(function WordControl(props, ref) {
    const { wordDistribution, letterState, indexPositions, onChange, wordId, onFocus, onKeyDown, onAnswer } = props;
    const [currentFocusedIndex, setCurrentFocusedIndex] = useState(null);
    const deferredFocus = useDeferredValue(currentFocusedIndex, { timeoutMs: 500 });
    const inputElements = useRef({});
    const [changesAfterCheck, setChangesAfterCheck] = useState(0);
    const isWordChecked = wordDistribution.every((letter) => letterState[letter.id].correct !== null);
    const posiblePutLetterCorrect = useRef(false);
    const dispatch = useDispatch();
    const view = useSelector((state) => state.game.view);
    const gameSounds = useGameSounds();
    const lastInputFocusAllow = useRef(true);
    const renderID = useRef(0);


    useEffect(() => {
        //MEDIDA DE SEGURIDAD PARA QUE NO SE PUEDA SEGUIR ESCRIBIENDO CUANDO ESTAMOS EN SCORE SCREEN
        if (view === SCREENS.SCORE_SCREEN) {
            if (currentFocusedIndex === null) return;
            setTimeout(() => {
                inputElements.current[currentFocusedIndex].blur();
                setCurrentFocusedIndex(null);
            });
        }
    }, [currentFocusedIndex, view])

    useImperativeHandle(ref, () => ({
        focus: (letterID) => {
            const index = wordDistribution.findIndex(letter => letter.id === letterID);
            setCurrentFocusedIndex(index);
        },
        focusEnd: () => {
            const wordDistributionReverse = wordDistribution.slice().reverse();
            const index = wordDistributionReverse.findIndex(letter => !letterState[letter.id].correct);
            const indexFocus = wordDistribution.length - index - 1;
            setCurrentFocusedIndex(indexFocus);
        },
        blur: () => {
            if (currentFocusedIndex === null) return;
            inputElements.current[currentFocusedIndex].blur();
            setCurrentFocusedIndex(null);
        }
    }), [wordDistribution, letterState, currentFocusedIndex])

    const handleDelete = (id) => {

        const prevVal = letterState[id].value;
        const newLetters = { ...letterState, [id]: { ...letterState[id], value: "" } };

        onChange(newLetters);

        if (!prevVal) {
            const currentIndex = wordDistribution.findIndex(letter => letter.id === id);
            if (currentIndex === 0) return;

            const prevIndex = findPreviousFocusIndex(wordDistribution, currentIndex, newLetters);
            const prevNewLetters = { ...letterState, [wordDistribution[prevIndex].id]: { ...letterState[wordDistribution[prevIndex].id], value: "" } };
            onChange(prevNewLetters);
            setCurrentFocusedIndex(prevIndex);
        }
    }

    const handleKeyDown = (event, id) => {
        if (event.key === ' ') {
            event.preventDefault();
            return;
        }
        if (event.key === 'Backspace') {
            event.preventDefault();
            handleDelete(id);
            return;
        }
        onKeyDown({ event, id, originalWordID: wordId });
    }
    const handleOnChange = async (event, id) => {
        const currentValue = event.target.value;
        if (!currentValue) return;
        const letter = getLastCharacter(currentValue);
        const value = letter.toUpperCase();
        const normalizedValue = normaliceWord(value);
        if (posiblePutLetterCorrect.current) {
            const prevIndex = currentFocusedIndex === 0 ? null : currentFocusedIndex - 1;
            if (!wordDistribution[prevIndex]) return;
            const prevID = wordDistribution[prevIndex].id;
            const value = normaliceWord(wordDistribution[prevIndex].value);
            const currentCorrectValue = normaliceWord(wordDistribution[currentFocusedIndex].value);
            if (value === normalizedValue && letterState[prevID].correct === true && currentCorrectValue !== normalizedValue) {
                posiblePutLetterCorrect.current = false;
                return;
            }
        }

        const newLetters = { ...letterState, [id]: { ...letterState[id], value: value } };
        const hasAnswered = await onChange(newLetters);

        gameSounds.play(CRUCIGRAMA_SOUNDS.WRITE);
        if (isWordChecked) setChangesAfterCheck(changesAfterCheck + 1);


        const nextFocusIndex = wordDistribution.every((letter) => newLetters[letter.id].correct === true) ?
            null : findNextFocusIndex(wordDistribution, currentFocusedIndex, newLetters);
        const lastLetterIndex = wordDistribution.length - 1;

        //RESET
        posiblePutLetterCorrect.current = (nextFocusIndex - currentFocusedIndex) > 1;

        if (currentFocusedIndex === lastLetterIndex && lastInputFocusAllow.current) {
            lastInputFocusAllow.current = false;
            return;
        } else if (currentFocusedIndex !== lastLetterIndex) {
            lastInputFocusAllow.current = true;
        }

        if (!hasAnswered && nextFocusIndex !== currentFocusedIndex) {
            setCurrentFocusedIndex(nextFocusIndex);
        } else if (hasAnswered) {
            renderID.current++;
        }
    }

    const handleFocus = (index) => {
        gameSounds.play(NAME_SOUND_ACTIVE);
        onFocus({ wordId, letterId: wordDistribution[index].id });
        if (deferredFocus === null) {
            const prevIndex = index === 0 ? null : index - 1;
            const isCorrectPrev = prevIndex !== null && letterState[wordDistribution[prevIndex].id].correct === true;
            if (isCorrectPrev) posiblePutLetterCorrect.current = true;
        }
        setCurrentFocusedIndex(index);
        inputElements.current[index].select();
        setTimeout(() => {
            if (!inputElements.current[index]) return;
            inputElements.current[index].setSelectionRange(1, 1);
        });
    }

    useEffect(() => {
        if (deferredFocus === null) {
            lastInputFocusAllow.current = false;
            if (changesAfterCheck > 0) {
                const isCorrect = wordDistribution.every((letter) => normaliceWord(letterState[letter.id].value) === normaliceWord(letter.value));
                if (!isCorrect) {
                    const userAnswer = wordDistribution.map(letter => letterState[letter.id].value).join('');
                    onAnswer(userAnswer, { questionIndexAnswer: wordId, nextQuestionIndex: wordId, delayLiveAnimation: 300 });
                }
                setChangesAfterCheck(0);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deferredFocus])

    useEffect(() => {
        if (currentFocusedIndex !== null) {
            inputElements.current[currentFocusedIndex].focus();
            dispatch(goToQuestion(wordId));
        }
    }, [currentFocusedIndex, wordId, dispatch])

    return (
        wordDistribution.map((letter, index) => {
            const isFocused = currentFocusedIndex !== null;
            const isCorrect = letterState[letter.id].correct === true;
            return (
                <Cell
                    key={index}
                    id={letter.id}
                    className={{
                        [classes.wordfocus]: isFocused,
                        [classes.correct]: isCorrect
                    }}
                    state={letterState[letter.id].correct}
                    isFirst={index === 0}
                    wordID={wordId}
                    renderID={renderID.current}
                    number={indexPositions[letter.id] ?? null}
                    ref={(element) => inputElements.current[index] = element}
                    onFocus={() => handleFocus(index)}
                    onClick={() => inputElements.current[index].setSelectionRange(1, 1)}
                    onKeyDown={(event) => handleKeyDown(event, letter.id)}
                    value={letterState[letter.id].value}
                    onChange={(event) => handleOnChange(event, letter.id)}
                    onBlur={() => {
                        setCurrentFocusedIndex(null);
                        onFocus(null);
                    }}
                />
            )
        })
    )
})